<script lang="ts">
  import { getPublicUpgradeIntent } from "$lib/api/endpoints";
  import { AppError } from "$lib/api/problem";
  import { router } from "../../router.svelte";
  import AuthLayout from "../auth/AuthLayout.svelte";
  import Button from "$lib/components/Button.svelte";
  import Banner from "$lib/components/Banner.svelte";

  // The landing page after Stripe. There is no session here — the visitor may never
  // have signed in on this device — so the only call made is the public, claim-scoped
  // intent lookup. Nothing session-guarded may be reached from this route.
  type Phase = "invalid" | "polling" | "exhausted" | "fulfilled" | "canceled" | "expired" | "error";

  const MAX_ATTEMPTS = 10;
  const INTERVAL_MS = 2500;
  const GENERIC = "Something went wrong. Please try again.";

  const PLAN_NAMES: Record<string, string> = {
    pro: "Pro",
    team: "Team",
    builders: "Builders",
    enterprise: "Enterprise"
  };

  const query = $derived(router.match.query);
  const intentId = $derived(readParam(query, "intent"));
  const claim = $derived(readParam(query, "claim"));

  function readParam(params: URLSearchParams, key: string): string {
    return (params.get(key) ?? "").trim();
  }

  // Seeded from the landing URL rather than from the derived values, so the very first
  // paint already shows the right copy: an effect only runs after that paint, and a
  // visitor arriving without a link must never be shown "Finishing your upgrade".
  let phase = $state<Phase>(initialPhase());

  function initialPhase(): Phase {
    const params = router.match.query;
    return readParam(params, "intent") && readParam(params, "claim") ? "polling" : "invalid";
  }
  let plan = $state<string | null>(null);
  let error = $state<AppError | null>(null);
  /** Bumped by "Check again" / "Try again" to re-enter the polling effect. */
  let pollToken = $state(0);
  // Plain, non-reactive: once a poll has started, a later empty query means the params
  // were scrubbed by us, not that the visitor arrived without a link.
  let started = false;

  const planName = $derived(plan ? (PLAN_NAMES[plan] ?? "your new plan") : "your new plan");

  const copy = $derived.by((): { title: string; subtitle: string } => {
    switch (phase) {
      case "invalid":
        return { title: "This link isn't valid", subtitle: "The upgrade details are missing." };
      case "exhausted":
        return { title: "Still finishing up", subtitle: "Your payment went through." };
      case "fulfilled":
        return { title: "You're upgraded", subtitle: `Welcome to ${planName}.` };
      case "canceled":
        return { title: "Upgrade canceled", subtitle: "No charge was made." };
      case "expired":
        return { title: "This link has expired", subtitle: "Upgrade links are short-lived." };
      case "error":
        return { title: "Couldn't check your upgrade", subtitle: "The upgrade status is unknown." };
      default:
        return { title: "Finishing your upgrade", subtitle: "This usually takes a few seconds." };
    }
  });

  /** setTimeout that rejects on abort, so teardown never leaves a timer armed. */
  function wait(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      let timer: ReturnType<typeof setTimeout>;
      const onAbort = (): void => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      };
      timer = setTimeout(() => {
        signal.removeEventListener("abort", onAbort);
        resolve();
      }, ms);
      signal.addEventListener("abort", onAbort, { once: true });
    });
  }

  // Only the genuinely terminal outcomes scrub. "exhausted" and "error" both offer a
  // retry, and the retry needs `intent` and `claim` to still be in the URL.
  function settle(): void {
    router.scrubQuery(["intent", "claim"]);
  }

  async function poll(id: string, claimToken: string, signal: AbortSignal): Promise<void> {
    try {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        if (attempt > 0) await wait(INTERVAL_MS, signal);
        const intent = await getPublicUpgradeIntent(id, claimToken, signal);
        if (signal.aborted) return;
        if (intent.status !== "pending") {
          plan = intent.plan;
          phase = intent.status === "fulfilled" ? "fulfilled" : "canceled";
          settle();
          return;
        }
      }
      phase = "exhausted";
    } catch (err) {
      if (signal.aborted) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (err instanceof AppError && err.code === "intent_expired") {
        error = null;
        phase = "expired";
        settle();
        return;
      }
      error = err instanceof AppError ? err : null;
      phase = "error";
    }
  }

  $effect(() => {
    const id = intentId;
    const claimToken = claim;
    // Read so a retry re-runs the effect.
    pollToken;
    if (!id || !claimToken) {
      if (!started) phase = "invalid";
      return;
    }
    started = true;
    phase = "polling";
    error = null;
    const controller = new AbortController();
    void poll(id, claimToken, controller.signal);
    return () => controller.abort();
  });

  function retry(): void {
    pollToken += 1;
  }
</script>

<AuthLayout title={copy.title} subtitle={copy.subtitle}>
  {#if phase === "polling"}
    <p class="upgrade-copy" role="status" aria-live="polite">
      We received your payment and we're updating your account now. This page refreshes
      itself — you can leave it open.
    </p>
  {:else if phase === "exhausted"}
    <p class="upgrade-copy" role="status" aria-live="polite">
      Your payment went through, and your account will update shortly. You can check
      again now, or sign in later — nothing is lost.
    </p>
    <Button onclick={retry} fullWidth>Check again</Button>
    <Button href="/login" variant="ghost" fullWidth>Sign in to the portal</Button>
  {:else if phase === "fulfilled"}
    <p class="upgrade-copy">
      Your upgrade is live on your account.
    </p>
    <p class="upgrade-copy">
      Return to VS Code — EpiDev updates automatically.
    </p>
    <Button href="/login" fullWidth>Sign in to the portal</Button>
  {:else if phase === "canceled"}
    <p class="upgrade-copy">
      The upgrade was canceled before it completed, so nothing changed on your account
      and no charge was made. You can start it again whenever you're ready.
    </p>
    <Button href="/login" fullWidth>Sign in to the portal</Button>
  {:else if phase === "expired"}
    <p class="upgrade-copy">
      Start the upgrade again from the portal or from EpiDev in VS Code, and you'll get
      a fresh link.
    </p>
    <Button href="/login" fullWidth>Sign in to the portal</Button>
  {:else if phase === "error"}
    <Banner
      tone="error"
      title="Couldn't check your upgrade"
      correlationId={error?.correlationId}
    >
      {error?.userMessage ?? GENERIC}
      {#snippet actions()}
        <Button size="sm" onclick={retry}>Try again</Button>
      {/snippet}
    </Banner>
    <Button href="/login" variant="ghost" fullWidth>Sign in to the portal</Button>
  {:else}
    <p class="upgrade-copy">
      This upgrade link is missing the details we need to look it up. Open the newest
      link from your confirmation email, or start the upgrade again from the portal.
    </p>
    <Button href="/login" fullWidth>Sign in to the portal</Button>
  {/if}
</AuthLayout>

<style>
  .upgrade-copy {
    font-size: 14.5px;
    color: var(--text);
    margin: 0;
  }
</style>
